import * as yup from 'yup';
import {
  combineRegex,
  isValidAmexCardNumberV2,
} from '@americanexpress/axp-field-validations-common';
import {
  alphaLower,
  alphaUpper,
  specialAlpha,
  apostrophe,
  hyphen,
  space,
  numbers,
  forwardSlash,
} from '../regular-expressions';
import { createMonthYearSchema } from './financial';
import { countryNameCodes } from '../utils';

// NOTE: UI has two fields that are combined into one on submit.
// Two validation sets are maintained.
export const STREET_MAXLENGTH = 34;
export const STREET_CHARSET = [numbers, alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen];
export const HOUSE_NUMBER_MAXLENGTH = 5;
export const HOUSE_NUMBER_CHARSET = [numbers, alphaLower, alphaUpper, forwardSlash, space, hyphen];
export const STREET_AND_HOUSE_NUMBER_LENGTH = STREET_MAXLENGTH + HOUSE_NUMBER_MAXLENGTH + 1;

// NOTE: combined street and house number charsets.
// Be careful with hyphen! If it's not the last in the regex, it'll be invalid
export const STREET_AND_HOUSE_CHARSET = [
  alphaLower,
  alphaUpper,
  specialAlpha,
  apostrophe,
  space,
  numbers,
  forwardSlash,
  hyphen,
];

const createMobileNumberSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}mobilePhoneNumber.required.error`)
    .min(5, `${errorMessagePrefix}mobilePhoneNumber.min.error`)
    .max(15, `${errorMessagePrefix}mobilePhoneNumber.max.error`)
    .test('is-zeros', `${errorMessagePrefix}mobilePhoneNumber.zeros.error`, (value) => !value.match(/^0+$/))
    .matches(/^\d+$/, `${errorMessagePrefix}mobilePhoneNumber.regex.error`);
};

const createStreetSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}street.required.error`)
    .min(1, `${errorMessagePrefix}street.min.error`)
    .max(STREET_MAXLENGTH, `${errorMessagePrefix}street.max.error`)
    .matches(combineRegex(STREET_CHARSET), `${errorMessagePrefix}street.regex.error`);
};

const createHouseNumberSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}houseNumber.required.error`)
    .min(1, `${errorMessagePrefix}houseNumber.min.error`)
    .max(HOUSE_NUMBER_MAXLENGTH, `${errorMessagePrefix}houseNumber.max.error`)
    .matches(combineRegex(HOUSE_NUMBER_CHARSET), `${errorMessagePrefix}houseNumber.regex.error`);
};

const createAddressLine1Schema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}addresses.addressline1.required.error`)
    .min(1, `${errorMessagePrefix}addresses.addressline1.min.error`)
    .max(STREET_AND_HOUSE_NUMBER_LENGTH, `${errorMessagePrefix}addresses.addressline1.max.error`)
    .matches(combineRegex(STREET_AND_HOUSE_CHARSET), `${errorMessagePrefix}addresses.addressline1.addressline1.regex.error`);
};

const createAddressLine2Schema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .notRequired()
    .min(1, `${errorMessagePrefix}addressLine2.min.error`)
    .max(40, `${errorMessagePrefix}addressLine2.max.error`)
    .matches(combineRegex([numbers, alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen]), `${errorMessagePrefix}addressLine2.regex.error`);
};

const createPostalCodeSchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}postalCode.required.error`)
    .min(5, `${errorMessagePrefix}postalCode.min.error`)
    .max(5, `${errorMessagePrefix}postalCode.max.error`)
    .matches(combineRegex([numbers]), `${errorMessagePrefix}postalCode.regex.error`);
};

const createCitySchema = ({ errorMessagePrefix = '' } = {}) => {
  return yup.string()
    .required(`${errorMessagePrefix}city.required.error`)
    .min(1, `${errorMessagePrefix}city.min.error`)
    .max(30, `${errorMessagePrefix}city.max.error`)
    .matches(combineRegex([alphaUpper, alphaLower, specialAlpha, apostrophe, space, hyphen]), `${errorMessagePrefix}city.regex.error`);
};

const mobilePhoneNumber = createMobileNumberSchema();
const street = createStreetSchema();
const houseNumber = createHouseNumberSchema();
const addressline1 = createAddressLine1Schema();
const addressLine2 = createAddressLine2Schema();
const postalCode = createPostalCodeSchema();
const city = createCitySchema();

const residenceStatus = yup.string()
  .required('residenceStatus.required.error')
  .oneOf(['RENT', 'OWN', 'OTHERS'], 'residenceStatus.enum.error');

const residenceSince = createMonthYearSchema({ errorMessagePrefix: 'residenceSince.' });

const emailOptIn = yup.boolean().notRequired();

const telemarketingOptIn = yup.boolean().notRequired();

const mothersMaidenName = yup.string()
  .required('mothersMaidenName.required.error')
  .min(2, 'mothersMaidenName.min.error')
  .max(20, 'mothersMaidenName.max.error')
  .matches(combineRegex([alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen]), 'mothersMaidenName.regex.error');

const hasSameAddress = yup.boolean().required();

const existingCardNumber = yup.boolean().notRequired();

const supplementaryStreetConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createStreetSchema({ errorMessagePrefix: 'supplementary.' });
    } else {
      return yup.string().notRequired();
    }
  });
};

const supplementaryHouseNumberConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createHouseNumberSchema({ errorMessagePrefix: 'supplementary.' });
    } else {
      return yup.string().notRequired();
    }
  });
};

const supplementaryHouseNameConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createAddressLine2Schema({ errorMessagePrefix: 'supplementary.' });
    } else {
      return yup.string().notRequired();
    }
  });
};


const supplementaryPostalCodeConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createPostalCodeSchema({ errorMessagePrefix: 'supplementary.' });
    } else {
      return yup.string().notRequired();
    }
  });
};

const supplementaryCityConditional = () => {
  return yup.string().when('hasSameAddress', ([value]) => {
    if(value === false){
      return createCitySchema({ errorMessagePrefix: 'supplementary.' });
    } else {
      return yup.string().notRequired();
    }
  });
};

const placeOfBirth = yup.string()
  .required('placeOfBirth.required.error')
  .min(1, 'placeOfBirth.min.error')
  .max(30, 'placeOfBirth.max.error')
  .matches(combineRegex([alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen]), 'placeOfBirth.regex.error');

const nationality = yup.string()
  .transform((value) => (value === '' ? undefined : value))
  .required('nationality.required.error')
  .oneOf(Object.values(countryNameCodes), 'nationality.enum.error');


  const cardNumberConditional = () => {
    return yup.string().when('existingCardNumber', ([value]) => {
      if(value === true){
        return yup.string()
               .required('cardNumber.required.error')
               .min(15, 'cardNumber.min.error')
               .test('validCardNumber', 'cardNumber.invalid.error', (value) => isValidAmexCardNumberV2(value));
      } else {
        return yup.string().notRequired();
      }
    });
  };
const cardNumber = cardNumberConditional();

const supplementary = {
  mobilePhoneNumber: createMobileNumberSchema({ errorMessagePrefix: 'supplementary.' }),
  hasSameAddress: hasSameAddress,
  street: supplementaryStreetConditional(),
  houseNumber: supplementaryHouseNumberConditional(),
  postalCode: supplementaryPostalCodeConditional(),
  city: supplementaryCityConditional(),
  houseName: supplementaryHouseNameConditional(),

  placeOfBirth: yup.string()
  .required('supplementary.placeOfBirth.required.error')
  .min(1,'supplmentary.placeOfBirth.min.error')
  .max(30,'supplementary.placeOfBirth.max.error')
  .matches(combineRegex([alphaLower, alphaUpper, specialAlpha, apostrophe, space, hyphen]), 'supplementary.placeOfBirth.regex.error'),

  nationality: yup.string()
  .transform((value) => (value === '' ? undefined : value))
  .required('supplementary.nationality.required.error')
  .oneOf(Object.values(countryNameCodes), 'supplementary.nationality.enum.error'),
};

export {
  mobilePhoneNumber,
  street,
  houseNumber,
  addressline1,
  addressLine2,
  postalCode,
  city,
  residenceStatus,
  residenceSince,
  emailOptIn,
  telemarketingOptIn,
  mothersMaidenName,
  placeOfBirth,
  nationality,
  existingCardNumber,
  cardNumber,
  supplementary,
};
